import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

const STORAGE_KEY_PREFIX = "docustudio:recent:";
const MAX = 12;

/**
 * Tracks recently-visited items of a given kind ("collections", "tags",
 * "notebooks", "documents"). Source of truth is server-side via
 * /v2/me/preferences/recent-collections for collections; other kinds fall back
 * to localStorage. Server roundtrip is best-effort; local cache wins for UX.
 *
 * Returns: { items, record(id), clear() }
 */
export function useRecentItems(kind: "collections" | "tags" | "notebooks" | "documents") {
  const storageKey = STORAGE_KEY_PREFIX + kind;
  const [items, setItems] = useState<string[]>(() => readLocal(storageKey));

  // Hydrate from server for collections (only kind currently persisted server-side).
  useEffect(() => {
    if (kind !== "collections") return;
    let cancelled = false;
    api
      .getRecentCollections()
      .then((r) => {
        if (cancelled) return;
        if (r.slugs.length > 0) {
          setItems((current) => mergeRecents(r.slugs, current));
          writeLocal(storageKey, mergeRecents(r.slugs, readLocal(storageKey)));
        }
      })
      .catch(() => {
        /* server unavailable, local wins */
      });
    return () => {
      cancelled = true;
    };
  }, [kind, storageKey]);

  const record = useCallback(
    (id: string) => {
      if (!id) return;
      setItems((current) => {
        const next = [id, ...current.filter((x) => x !== id)].slice(0, MAX);
        writeLocal(storageKey, next);
        if (kind === "collections") {
          void api.setRecentCollections(next).catch(() => {
            /* best-effort */
          });
        }
        return next;
      });
    },
    [kind, storageKey],
  );

  const clear = useCallback(() => {
    setItems([]);
    writeLocal(storageKey, []);
    if (kind === "collections") {
      void api.setRecentCollections([]).catch(() => {});
    }
  }, [kind, storageKey]);

  return { items, record, clear };
}

function readLocal(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((x): x is string => typeof x === "string");
    return [];
  } catch {
    return [];
  }
}

function writeLocal(key: string, value: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota / disabled storage */
  }
}

function mergeRecents(server: string[], local: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of [...server, ...local]) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= MAX) break;
  }
  return out;
}

// Pure helper exported for tests
export const __recentTest__ = { mergeRecents };
